/**
 * Textos da Home que dependem de números: "1 lista" × "3 listas" e a frase das cidades
 * ("As de negócios locais cobrem 11 cidades em 5 estados."). O português fica como padrão no
 * código; en, es e fr vêm de src/i18n/locales.
 */
import { formatNumber } from '../../lib/format';
import { BRAZIL_STATES } from './brazilStates';
import type { Coverage } from './homeData';

export type Translate = (key: string, options: Record<string, unknown>) => string;

const LOCALES: Record<string, string> = { pt: 'pt-BR', en: 'en-US', es: 'es-ES', fr: 'fr-FR' };

export function localeOf(lang: string): string {
  return LOCALES[lang] ?? 'pt-BR';
}

export type CountKey = 'contacts' | 'lists' | 'cities' | 'states' | 'days';

const COUNT_DEFAULTS: Record<CountKey, [string, string]> = {
  contacts: ['{{n}} contato', '{{n}} contatos'],
  lists: ['{{n}} lista', '{{n}} listas'],
  cities: ['{{n}} cidade', '{{n}} cidades'],
  states: ['{{n}} estado', '{{n}} estados'],
  days: ['{{n}} dia', '{{n}} dias'],
};

/** "1 lista" ou "3 listas" (em francês, o zero também fica no singular). */
export function countText(t: Translate, key: CountKey, n: number, lang: string): string {
  const one = lang === 'fr' ? n <= 1 : n === 1;
  const [singular, plural] = COUNT_DEFAULTS[key];
  return t(`home3b_${key}_${one ? 'one' : 'other'}`, {
    defaultValue: one ? singular : plural,
    n: formatNumber(n, localeOf(lang)),
  });
}

const UF_BY_SIGLA = new Map(Object.values(BRAZIL_STATES).map((s) => [s.uf, s]));

/** Até três estados aparecem pelo nome ("no Paraná e em Santa Catarina"). */
const NAMED_STATES_MAX = 3;

function joinNames(t: Translate, names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  const and = t('home3b_and', { defaultValue: ' e ' });
  return `${names.slice(0, -1).join(', ')}${and}${names[names.length - 1]}`;
}

/**
 * Frase das listas de negócios locais. Com locais fora do Brasil (a tabela de municípios só
 * cobre o Brasil), conta só as cidades.
 */
export function coverageText(t: Translate, coverage: Coverage, lang: string): string {
  if (coverage.cities === 0) return '';
  const cities = countText(t, 'cities', coverage.cities, lang);
  if (coverage.abroad || coverage.ufs.length === 0) {
    return t('home3b_cover', { defaultValue: 'As de negócios locais cobrem {{cities}}.', cities });
  }
  const states = coverage.ufs.map((uf) => UF_BY_SIGLA.get(uf)).filter((s) => s !== undefined);
  if (states.length <= NAMED_STATES_MAX && (lang === 'pt' || lang === 'en' || lang === 'es')) {
    const where =
      lang === 'pt'
        ? joinNames(t, states.map((s) => s.inPt))
        : t('home3b_in_named', { defaultValue: 'em {{states}}', states: joinNames(t, states.map((s) => s.name)) });
    return t('home3b_cover_named', {
      defaultValue: 'As de negócios locais cobrem {{cities}} {{where}}.',
      cities,
      where,
    });
  }
  return t('home3b_cover_count', {
    defaultValue: 'As de negócios locais cobrem {{cities}} em {{states}}.',
    cities,
    states: countText(t, 'states', coverage.ufs.length, lang),
  });
}
